// French translations of the game content. English is the source language: the core, the API and the game server
// produce English text. Keys: core identifiers (see ContentDict in content.ts); `phrases` and `errors` are keyed by
// the exact English text, with the French as value.
import type { ContentDict } from './content.js';
import { errorPatterns, systemChatPatterns, exactErrors } from './content-fr-errors.js';
import { libraryEvents, calendarEvents, eventJerseys } from './content-fr-events.js';

const fr: ContentDict = {
  items: {
    turbo: { name: 'Turbo', help: '+50 % de puissance pendant 4 s, le home-trainer s’allège' },
    banana: { name: 'Banane', help: 'Posée derrière toi. Qui roule dessus glisse et a les jambes lourdes' },
    shell: { name: 'Missile', help: 'Fonce sur le coureur juste devant toi et touche tout ce qu’il croise' },
    shield: { name: 'Bouclier', help: 'Bloque la prochaine attaque pendant 10 s' },
    magnet: { name: 'Aimant d’aspi', help: 'Aspiration maximale pendant 8 s, même seul' },
    jam: { name: 'Dérailleur grippé', help: 'Bloque les vitesses du coureur devant toi pendant 6 s' },
    storm: { name: 'Vent de face', help: 'Un orage souffle sur le leader pendant 5 s' },
    fog: { name: 'Brouillard', help: 'Aveugle et ralentit tous les coureurs devant toi pendant 4 s' },
    bomb: { name: 'Bombe de watts', help: 'Sprinte 5 s : plus ta moyenne dépasse ta FTP, plus le boost est fort' },
    crash: { name: 'Chute', help: 'Fait tomber le coureur juste devant toi. Qui roule sur lui tombe aussi' },
  },
  effects: {
    turbo: '⚡ Turbo', bomb: '💥 Boost', charge: '💣 Sprinte !', magnet: '🧲 Aspi',
    heavy: '🦵 Jambes lourdes', spin: '💫 Sonné', jam: '🔒 Vitesses bloquées', headwind: '⛈️ Vent de face', fog: '🌫️ Brouillard', cheer: '📣 Public', crash: '🤕 À terre',
  },
  modes: {
    race: { name: 'Grand Prix', desc: 'Course classique avec objets. Premier à franchir la ligne.' },
    tt: { name: 'Contre-la-montre', desc: 'Chacun pour soi contre les bots : ni objets, ni aspiration.' },
    ttt: { name: 'Contre-la-montre par équipes', desc: 'Des équipes de quatre partent à une minute d’intervalle ; sans objets, aspiration entre coéquipiers seulement. Le chrono de l’équipe s’arrête sur son deuxième coureur.' },
    elim: { name: 'Élimination', desc: 'À chaque tour du leader, le dernier est éliminé.' },
    ghost: { name: 'Chrono fantôme', desc: 'Contre-la-montre sans objets contre ton meilleur passage.' },
    free: { name: 'Sortie libre', desc: 'Roule en groupe à l’allure de ton choix, sans objets ni ligne d’arrivée.' },
    workout: { name: 'Séance', desc: 'Entraînement structuré, mode ERG.' },
  },
  difficulty: { facile: 'Facile', normal: 'Normal', difficile: 'Difficile', pro: 'Pro' },
  tracks: {
    'everest-road': { name: 'Route de l’Everest', desc: '19,6 km · un col de 600 m en lacets, à 4 300 m d’altitude' },
    observatoire: { name: 'Avenue Circulaire', desc: '1,26 km · la boucle autour de l’Observatoire royal, 7 m de dénivelé' },
    collines: { name: 'Circuit des Collines', desc: '2,7 km · une montée de 7 % puis un faux plat' },
    mur: { name: 'Boucle du Mur', desc: '2,1 km · plat rapide et un mur court de 6 %' },
    nuages: { name: 'Col des Nuages', desc: '3,2 km · un vrai col de 90 m, pentes jusqu’à 10 %' },
    crit: { name: 'Critérium de la Ville', desc: '1,6 km · plat, virages serrés, idéal pour les sprinters' },
    coast: { name: 'Route de la Côte', desc: '5,0 km · plat au bord de la mer, de longues lignes droites pour un gros peloton' },
    ardennes: { name: 'Boucle ardennaise', desc: '4,6 km · deux côtes courtes, la seconde un mur à 10 %' },
    forest: { name: 'Bosses de la Forêt', desc: '3,4 km · routes vallonnées sous les arbres, jamais plat longtemps' },
    valley: { name: 'Route de la Vallée', desc: '6,9 km de A à B · la vallée en descente, une courte côte, un sprint en ville' },
    summit: { name: 'Route du Sommet', desc: '6,5 km de A à B · 440 m de montée en quatre lacets, arrivée au sommet' },
  },
  workouts: {
    decouverte: { name: 'Découverte', desc: 'Une première séance douce pour apprivoiser le mode ERG.' },
    endurance60: { name: 'Endurance 60 min', desc: 'Volume aérobie en zone 2, cadence variée.' },
    tempo45: { name: 'Tempo 45 min', desc: 'Deux blocs soutenus en zone 3.' },
    ss2x20: { name: 'Sweet Spot 2×20', desc: 'Le classique pour faire monter la FTP sans trop de fatigue.' },
    seuil3x10: { name: 'Seuil 3×10', desc: 'Trois blocs au seuil, récupération 5 min.' },
    overunder: { name: 'Over-Under', desc: 'Alterner au-dessus et en dessous du seuil pour apprendre à évacuer le lactate.' },
    'vo2-5x3': { name: 'VO2max 5×3', desc: 'Cinq efforts de 3 minutes à 115 %.' },
    pyramide: { name: 'Pyramide', desc: '1-2-3-4-3-2-1 minutes à 110 %.' },
    tabata: { name: 'Tabata', desc: 'Deux séries de 8 × (20 s à fond / 10 s).' },
    sprints: { name: 'Sprints 8×15 s', desc: 'Huit sprints courts, récupération complète.' },
    recup30: { name: 'Récupération 30 min', desc: 'Faire tourner les jambes, très facile.' },
    ftp20: { name: 'Test FTP 20 min', desc: 'FTP estimée à 95 % de la moyenne des 20 minutes. Le bloc de test est en roue libre : donne tout.' },
    ramp: { name: 'Ramp test', desc: '+20 W chaque minute jusqu’à l’épuisement. FTP estimée à 72 % de la meilleure minute. Arrête quand tu ne tiens plus.' },
  },
  plans: {
    debut4: { name: 'Débuter en 4 semaines', desc: 'Trois séances par semaine pour construire les bases.' },
    ftp6: { name: 'FTP Builder 6 semaines', desc: 'Sweet spot et seuil progressifs, test au début et à la fin.' },
    sprint4: { name: 'Puissance & sprint', desc: 'Pour gagner les arrivées au sprint en Grand Prix.' },
    reprise3: { name: 'Reprise en douceur', desc: 'Trois semaines pour revenir après une coupure.' },
  },
  categories: { endurance: 'Endurance', tempo: 'Tempo', seuil: 'Seuil', vo2: 'VO2max', anaerobie: 'Anaérobie', recup: 'Récupération', test: 'Test' },
  zones: { 1: 'Récupération', 2: 'Endurance', 3: 'Tempo', 4: 'Seuil', 5: 'VO2max', 6: 'Anaérobie', 7: 'Neuromusculaire' },
  phrases: {
    'Progressive warm-up': 'Échauffement progressif',
    'Short accelerations': 'Petites accélérations',
    'Cool-down': 'Retour au calme',
    'Warm-up': 'Échauffement',
    'A bit of tempo': 'Un peu de tempo',
    'Free cadence': 'Cadence libre',
    'Cadence 100 rpm': 'Cadence 100 rpm',
    'Cadence 70 rpm': 'Cadence 70 rpm',
    'Steady tempo': 'Tempo régulier',
    'Sweet spot: steady, breathe': 'Sweet spot : régulier, respire',
    'Second block, hang in there': 'Deuxième bloc, tiens bon',
    'At threshold': 'Au seuil',
    'Over / under': 'Over / under',
    'VO2: very hard but steady': 'VO2 : très dur mais régulier',
    'All out!': 'À fond !',
    'Again!': 'Encore !',
    'Sprint!': 'Sprint !',
    'Very easy, smooth': 'Très facile, souple',
    'Openers': 'Ouverture',
    'TEST: 20 min all-out, steady': 'TEST : 20 min à fond, régulier',
    'Hold the cadence!': 'Tiens la cadence !',
    'Free ride': 'Roue libre',
    'Effort': 'Effort',
    'Recovery': 'Récupération',
  },
  achievements: {
    first: { name: 'Premiers tours de roue', desc: 'Termine ta première activité' },
    hour: { name: 'Une heure en selle', desc: 'Roule 60 minutes d’affilée' },
    km100: { name: 'Centurion', desc: '100 km au total' },
    km500: { name: 'Grand rouleur', desc: '500 km au total' },
    km1000: { name: 'Mille bornes', desc: '1 000 km au total' },
    climb1000: { name: 'Grimpeur', desc: '1 000 m de dénivelé au total' },
    win: { name: 'Première victoire', desc: 'Gagne un Grand Prix' },
    wins10: { name: 'Collectionneur', desc: '10 victoires, tous modes' },
    elim: { name: 'Dernier survivant', desc: 'Gagne une élimination' },
    online: { name: 'Connecté', desc: 'Termine une course en ligne' },
    onlinewin: { name: 'Champion du réseau', desc: 'Gagne une course en ligne' },
    sniper: { name: 'Sniper', desc: '5 touches dans une même course' },
    hits50: { name: 'Semeur de bananes', desc: '50 touches au total' },
    sprint: { name: 'Sprinteur', desc: 'Dépasse 12 W/kg en pointe' },
    workout: { name: 'Discipliné', desc: 'Termine une séance structurée' },
    workouts10: { name: 'Assidu', desc: '10 séances terminées' },
    test: { name: 'Mesuré', desc: 'Fais un test FTP ou un Ramp test' },
    ghost: { name: 'Chasseur de fantômes', desc: 'Bats ton propre record en chrono' },
    tss100: { name: 'Grosse journée', desc: '100 TSS en une activité' },
    streak3: { name: 'Régulier', desc: '3 jours d’affilée' },
    streak7: { name: 'Inarrêtable', desc: '7 jours d’affilée' },
    plan: { name: 'Diplômé', desc: 'Termine un plan d’entraînement' },
    everest: { name: 'Everesteur', desc: 'Grimpe 8 848 m sur la route de l’Everest' },
    observatoire: { name: 'Seigneur de l’Observatoire', desc: 'Roule 24 heures autour de l’Observatoire royal' },
  },
  challenges: {
    everest: { name: 'Everesting', desc: 'Grimpe 8 848 m, la hauteur de l’Everest, dans les lacets de la route du camp de base. 36 heures pour y arriver.' },
    observatoire: { name: '24 heures de l’Observatoire', desc: 'Roule 24 heures autour de l’Observatoire royal, à Uccle (Bruxelles), sur la boucle de l’avenue Circulaire. 36 heures au chrono, pauses comprises.' },
  },
  titles: { 1: 'Débutant', 3: 'Cyclo', 6: 'Rouleur', 10: 'Puncheur', 15: 'Champion', 20: 'Légende', 30: 'Mythe' },
  looks: {
    pattern: { uni: 'Uni', degrade: 'Dégradé', bandes: 'Bandes', pois: 'Pois', damier: 'Damier', arcenciel: 'Arc-en-ciel', jaune: 'Maillot jaune' },
    frame: { couleur: 'Couleur du maillot', noir: 'Carbone noir', blanc: 'Blanc', chrome: 'Chrome', or: 'Or' },
    helmet: { couleur: 'Couleur du maillot', blanc: 'Blanc', noir: 'Noir', or: 'Doré' },
  },
  unlocks: {
    'look:pattern:bandes': 'Maillot à bandes',
    'track::coast': 'Circuit : Route de la Côte',
    'track::nuages': 'Circuit : Col des Nuages',
    'track::valley': 'Circuit : Route de la Vallée',
    'track::ardennes': 'Circuit : Boucle ardennaise',
    'look:pattern:pois': 'Maillot à pois',
    'look:frame:chrome': 'Cadre chromé',
    'mood::coucher': 'Ambiance coucher de soleil',
    'track::forest': 'Circuit : Bosses de la Forêt',
    'track::summit': 'Circuit : Route du Sommet',
    'track::crit': 'Circuit : Critérium de la Ville',
    'look:pattern:damier': 'Maillot damier',
    'difficulty::pro': 'Bots niveau Pro',
    'look:helmet:or': 'Casque doré',
    'look:pattern:arcenciel': 'Maillot arc-en-ciel',
    'look:frame:or': 'Cadre en or',
    'look:pattern:jaune': 'Maillot jaune',
  },
  moods: { jour: 'Plein jour', golden: 'Heure dorée', coucher: 'Coucher de soleil', automne: 'Forêt d’automne', hiver: 'Col enneigé' },
  quality: { ultra: 'Ultra', haut: 'Haute', moyen: 'Moyenne', bas: 'Basse' },
  errors: {
    // API (HttpError and the error handler)
    'That’s your own code!': 'C’est ton propre code !',
    'Incorrect email or password': 'E-mail ou mot de passe incorrect',
    'Not signed in': 'Non authentifié',
    'Session expired, please sign in again': 'Session expirée, reconnecte-toi',
    'Activity not found': 'Activité introuvable',
    'No player with this code': 'Aucun joueur avec ce code',
    'Account not found': 'Compte introuvable',
    'Request not found': 'Demande introuvable',
    'Player not found': 'Joueur introuvable',
    'This account already has activities: import not possible': 'Ce compte contient déjà des activités : import impossible',
    'An account already exists with this email': 'Un compte existe déjà avec cet e-mail',
    'Too many pending requests: wait for a reply': 'Trop de demandes en attente : attends qu’on te réponde',
    'Too many attempts on this account, try again in 15 minutes': 'Trop de tentatives sur ce compte, réessaie dans 15 minutes',
    'This link is invalid or has expired': 'Ce lien est invalide ou a expiré',
    'Password reset is not available on this server': 'La réinitialisation du mot de passe n’est pas disponible sur ce serveur',
    'Could not generate a friend code': 'Impossible de générer un code ami',
    'Too many requests, try again in a moment': 'Trop de requêtes, réessaie dans un instant',
    'Internal error': 'Erreur interne',
    // Google or Strava sign-in, upload to Strava
    'Signing in with this service is not available on this server': 'Connexion par ce service non disponible sur ce serveur',
    'Sign-in link expired, please start again': 'Lien de connexion expiré, recommence',
    'This Google account is already linked to another player': 'Ce compte Google est déjà relié à un autre joueur',
    'This Strava account is already linked to another player': 'Ce compte Strava est déjà relié à un autre joueur',
    'Another Google account is already linked: unlink it first': 'Un autre compte Google est déjà relié : délie-le d’abord',
    'Another Strava account is already linked: unlink it first': 'Un autre compte Strava est déjà relié : délie-le d’abord',
    'An account already exists with this email: sign in with your password, then link Google in the settings': 'Un compte existe déjà avec cet e-mail : connecte-toi avec ton mot de passe, puis relie Google dans les réglages',
    'An account already exists with this email: sign in with your password, then link Strava in the settings': 'Un compte existe déjà avec cet e-mail : connecte-toi avec ton mot de passe, puis relie Strava dans les réglages',
    'This account is not linked': 'Ce compte n’est pas relié',
    'This is your only way to sign in: link another account before unlinking this one': 'C’est ta seule façon de te connecter : relie un autre compte avant de délier celui-ci',
    'Strava account not linked': 'Compte Strava non relié',
    'Strava is not available on this server': 'Strava n’est pas disponible sur ce serveur',
    'Strava did not grant permission to upload activities: link your account again': 'Strava n’a pas donné la permission d’envoyer des activités : relie ton compte à nouveau',
    'Strava connection expired: link your account again': 'Connexion à Strava expirée : relie ton compte à nouveau',
    'Activity has no recording: nothing to send': 'Activité sans enregistrement : rien à envoyer',
    'Strava is not responding, try again later': 'Strava ne répond pas, réessaie plus tard',
    'Strava rejected the file': 'Strava a refusé le fichier',
    // Contracts (zod messages)
    'Password: at least 8 characters': 'Mot de passe : 8 caractères minimum',
    'Unknown time zone': 'Fuseau horaire inconnu',
    'Invalid friend code': 'Code ami invalide',
    'Invalid identifier': 'Identifiant invalide',
    'Type at least 2 characters': 'Tape au moins 2 caractères',
    // Core (validateSamples)
    'Empty activity': 'Activité vide',
    'Activity too long (more than 24 h)': 'Activité trop longue (plus de 24 h)',
    'Recording not found': 'Enregistrement introuvable',
    'The recording is not at this offset': 'L’enregistrement n’en est pas là',
    'An activity already has this id': 'Une activité porte déjà cet identifiant',
    'Inconsistent samples': 'Échantillons incohérents',
    'Power out of range (0 to 2500 W)': 'Puissance hors limites (0 à 2500 W)',
    'Speed out of range': 'Vitesse hors limites',
    // Game server
    'Invalid message.': 'Message invalide.',
    'Server error, try again.': 'Erreur serveur, réessaie.',
    'Room not found. Check the code.': 'Salon introuvable. Vérifie le code.',
    'Choose a workout first.': 'Choisis d’abord une séance.',
    'A race is in progress in this room, try again when it ends.': 'Une course est en cours dans ce salon, réessaie à la fin.',
    'Room full (50 players).': 'Salon complet (50 joueurs).',
    'The host removed you from this room.': 'L’hôte t’a retiré de ce salon.',
    // Every other message (workshop, events, creations, moderation, core files)
    ...exactErrors,
  },
  errorPatterns,
  systemChat: systemChatPatterns,
  events: libraryEvents,
  calendar: calendarEvents,
  eventJerseys,
  // Official creations without a core table: games, theme descriptions, wheels. Keyed by catalogue identifier.
  official: {
    dynamo: { name: 'Défense Dynamo', desc: 'Tes watts alimentent le réseau : construis des tours et repousse 15 vagues. Plus ta défense est grosse, plus le home-trainer résiste. Sprinte pour surcharger.' },
    zeppelin: { name: 'Zeppelin', desc: 'Ta puissance règle l’altitude du dirigeable entre montagnes, orages et oiseaux. En séance, tenir la cible, c’est voler dans le couloir doré.' },
    neon: { name: 'Pédales Néon', desc: 'Des motos de lumière contre trois coureurs : tes watts font la vitesse, ta traînée enferme les autres. Saute un mur, sprinte pour le turbo.' },
    cadence: { name: 'Cadence Hero', desc: 'Jeu de rythme : tiens la cadence demandée, joue les notes en rythme avec les manettes et sprinte sur les éclairs. Forcer durcit la résistance.' },
    horde: { name: 'Horde', desc: 'Une horde de zombies te poursuit toute la nuit : tes watts font ta vitesse, les côtes la ramènent. Esquive, lance des pétards et tiens jusqu’à l’aube.' },
    pong: { name: 'Pong sur rouleaux', desc: 'Un pong contre l’ordinateur où la hauteur de ta raquette, c’est ta puissance : les balles hautes se rattrapent en sprintant. Tire pour smasher.' },
    jour: { name: 'Plein jour', desc: 'Grand soleil, ciel bleu, campagne verte.' },
    golden: { name: 'Heure dorée', desc: 'Soleil bas et chaud, longues ombres.' },
    coucher: { name: 'Coucher de soleil', desc: 'Ciel orange et rose à l’horizon.' },
    automne: { name: 'Forêt d’automne', desc: 'Feuillage roux, légère brume et champs labourés.' },
    hiver: { name: 'Col enneigé', desc: 'Neige qui tombe, sapins et lumière froide.' },
    'wheels-carbone': { name: 'Roues carbone', desc: '' },
  },
};

/**
 * Old French consignes (workout blocks and XP lines) mapped to English. Custom workouts copied from the built-ins and
 * activities saved before English became the source language still hold these French texts; in English they are
 * shown translated.
 */
export const legacyPhrases: Record<string, string> = {
    'Échauffement progressif': 'Progressive warm-up',
    'Petites accélérations': 'Short accelerations',
    'Retour au calme': 'Cool-down',
    'Échauffement': 'Warm-up',
    'Un peu de tempo': 'A bit of tempo',
    'Cadence libre': 'Free cadence',
    'Cadence 100 rpm': 'Cadence 100 rpm',
    'Cadence 70 rpm': 'Cadence 70 rpm',
    'Tempo régulier': 'Steady tempo',
    'Sweet spot : régulier, respire': 'Sweet spot: steady, breathe',
    'Deuxième bloc, tiens bon': 'Second block, hang in there',
    'Au seuil': 'At threshold',
    'Over / under': 'Over / under',
    'VO2 : très dur mais régulier': 'VO2: very hard but steady',
    'À fond !': 'All out!',
    'Encore !': 'Again!',
    'Sprint !': 'Sprint!',
    'Très facile, souple': 'Very easy, smooth',
    'Ouverture': 'Openers',
    'TEST : 20 min à fond, régulier': 'TEST: 20 min all-out, steady',
    'Tiens la cadence !': 'Hold the cadence!',
    'Roue libre': 'Free ride',
    'Effort': 'Effort',
    'Récupération': 'Recovery',
};

export default fr;
